import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Camera, LogOut, Menu, X, LayoutDashboard, Sun, Moon, User } from 'lucide-react';

const Navbar = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lightboxActive, setLightboxActive] = useState(false);
  const [dark, setDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      if (saved) return saved === 'dark';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  useEffect(() => {
    const handler = (e) => setLightboxActive(e.detail.open);
    window.addEventListener('lightbox', handler);
    return () => window.removeEventListener('lightbox', handler);
  }, []);

  const handleLogout = async () => {
    try { await axios.post('/api/auth/logout'); } catch {}
    setUser(null);
    setMobileOpen(false);
    navigate('/login');
  };

  const closeMobile = useCallback(() => setMobileOpen(false), []);
  useEffect(() => { closeMobile(); }, [location.pathname, closeMobile]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e) => { if (e.key === 'Escape') closeMobile(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mobileOpen, closeMobile]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* ── Desktop nav (pill-shaped) ── */}
      <nav className="hidden md:block absolute top-4 left-1/2 -translate-x-1/2 z-50 max-w-7xl px-4 w-[calc(100%-32px)]">
        <div className={`bg-surface/80 dark:bg-surface-container-highest/80 backdrop-blur-md border border-border/30 rounded-full shadow-soft-card px-6 py-3 flex justify-between items-center transition-all ${lightboxActive ? 'backdrop-blur-xl bg-surface/60' : ''}`}>
          <Link to="/" className="text-headline-md text-primary font-bold tracking-tight hover:scale-95 transition-transform duration-200">
            fotoSOP
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/"
              className={`text-body-lg font-bold hover:scale-95 transition-all duration-200 scale-95 active:scale-90 ${
                location.pathname === '/' ? 'text-primary border-b-2 border-primary-container' : 'text-on-secondary-container hover:text-primary'
              }`}>Home</Link>
            <Link to="/album"
              className={`text-body-lg font-bold hover:scale-95 transition-all duration-200 scale-95 active:scale-90 ${
                location.pathname === '/album' ? 'text-primary border-b-2 border-primary-container' : 'text-on-secondary-container hover:text-primary'
              }`}>Album</Link>
            <Link to="/developer"
              className={`text-body-lg font-bold hover:scale-95 transition-all duration-200 scale-95 active:scale-90 ${
                location.pathname === '/developer' ? 'text-primary border-b-2 border-primary-container' : 'text-on-secondary-container hover:text-primary'
              }`}>Developer</Link>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setDark(!dark)}
              className="p-2 text-on-secondary-container hover:text-primary hover:bg-primary-container/30 rounded-full transition-colors cursor-pointer"
              aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {user ? (
              <>
                <Link to="/dashboard"
                  className="text-label-md bg-primary-container text-on-primary-container px-5 py-2 rounded-full font-bold shadow-bubble hover:scale-95 active:scale-90 transition-all duration-200 flex items-center gap-1.5">
                  <LayoutDashboard size={15} /> Dashboard
                </Link>
                <button onClick={handleLogout} className="p-2 text-on-secondary-container hover:text-error hover:bg-error-container/30 rounded-full transition-colors cursor-pointer" aria-label="Logout">
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <Link to="/login"
                className="text-label-md bg-primary-container text-on-primary-container px-6 py-2 rounded-full font-bold shadow-bubble hover:scale-95 active:scale-90 transition-all duration-200">
                Masuk
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* ── Mobile nav (minimal: brand + hamburger + dark toggle) ── */}
      <nav className={`md:hidden fixed top-0 left-0 right-0 z-50 px-4 pt-3 pb-2 transition-all ${lightboxActive ? 'backdrop-blur-xl bg-surface/60' : ''}`}>
        <div className="bg-surface/80 dark:bg-surface-container-highest/80 backdrop-blur-md border border-border/30 rounded-2xl shadow-soft-card px-4 py-2.5 flex justify-between items-center">
          <Link to="/" className="text-headline-sm text-primary font-bold tracking-tight">
            fotoSOP
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => setDark(!dark)}
              className="p-2 text-on-secondary-container hover:text-primary hover:bg-primary-container/30 rounded-full transition-colors cursor-pointer"
              aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              className="flex items-center justify-center w-10 h-10 rounded-full bg-surface-container-low text-primary hover:bg-primary-container transition-colors cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Mobile backdrop ── */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:hidden animate-fade-in" onClick={closeMobile} aria-hidden="true" />
      )}

      {/* ── Mobile sidebar ── */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-surface/95 dark:bg-surface-container-highest/95 backdrop-blur-xl border-l border-border/30 z-50 md:hidden transform transition-transform duration-300 ease-out shadow-2xl ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog" aria-modal="true" aria-label="Navigation menu">
        <div className="flex justify-between items-center h-16 px-5 border-b border-border/30">
          <span className="text-headline-md text-primary font-bold">Menu</span>
          <button onClick={closeMobile} className="p-1.5 text-on-secondary-container hover:text-primary hover:bg-primary-container/30 rounded-full transition-colors cursor-pointer" aria-label="Close menu">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-1">
          <Link to="/"
            className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-body-md font-bold transition-all ${
              location.pathname === '/' ? 'bg-primary text-on-primary shadow-sm' : 'text-on-secondary-container hover:bg-surface-container-low hover:text-primary'
            }`}>Home</Link>
          <Link to="/album"
            className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-body-md font-bold transition-all ${
              location.pathname === '/album' ? 'bg-primary text-on-primary shadow-sm' : 'text-on-secondary-container hover:bg-surface-container-low hover:text-primary'
            }`}>Album</Link>
          <Link to="/developer"
            className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-body-md font-bold transition-all ${
              location.pathname === '/developer' ? 'bg-primary text-on-primary shadow-sm' : 'text-on-secondary-container hover:bg-surface-container-low hover:text-primary'
            }`}>Developer</Link>

          <div className="border-t border-border/30 my-3" />

          {user ? (
            <>
              <Link to="/dashboard"
                className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-body-md font-bold transition-all ${
                  isActive('/dashboard') ? 'bg-primary text-on-primary shadow-sm' : 'text-on-secondary-container hover:bg-surface-container-low hover:text-primary'
                }`}>
                <LayoutDashboard size={18} /> Dashboard
              </Link>
              <div className="flex items-center gap-3 px-4 py-2">
                <div className="w-8 h-8 bg-primary-container rounded-full flex items-center justify-center">
                  <User size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-body-md text-on-surface font-bold">{user.username}</p>
                  <p className="text-label-sm text-on-surface-variant font-bold">Admin</p>
                </div>
              </div>
              <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-body-md font-bold text-on-secondary-container hover:bg-error-container/30 hover:text-error transition-colors cursor-pointer">
                <LogOut size={18} /> Logout
              </button>
            </>
          ) : (
            <Link to="/login"
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-body-md font-bold transition-all ${
                isActive('/login') ? 'bg-primary text-on-primary shadow-sm' : 'text-on-secondary-container hover:bg-surface-container-low hover:text-primary'
              }`}>
              <LayoutDashboard size={18} /> Masuk
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;
